// src/hooks/useServerStatusAdmin.js
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * useServerStatusAdmin
 * - Pinga /api/admin/ping (o URL passato) a intervalli regolari.
 * - Considera online: qualsiasi 2xx (res.ok). 401/403 = server raggiungibile ma non autorizzato.
 * - Se manca il token, opzionalmente reindirizza alla login admin.
 *
 * @param {string} pingUrl            - Endpoint di ping admin (default: '/api/admin/ping')
 * @param {object} options
 * @param {number} options.intervalMs - Intervallo ping in ms (default: 60000)
 * @param {string} options.tokenKey   - Chiave localStorage per il token (default: 'accessToken')
 * @param {boolean} options.redirectOnUnauthorized - Se true, redirect su 401/403 (default: true)
 * @param {string} options.loginPath  - Path login admin (default: '/admin/login')
 * @param {function} options.onUnauthorized - Callback opzionale su 401/403 (es. logout redux)
 * @returns {boolean} isServerOnline
 */
export default function useServerStatusAdmin(
  pingUrl = '/api/admin/ping',
  {
    intervalMs = 60000,
    tokenKey = 'accessToken',
    redirectOnUnauthorized = true,
    loginPath = '/admin/login',
    onUnauthorized,
  } = {}
) {
  const [isServerOnline, setIsServerOnline] = useState(true);
  const navigate = useNavigate();
  const navOnceRef = useRef(false); // evita redirect multipli

  useEffect(() => {
    const controller = new AbortController();

    const ping = async () => {
      try {
        const token = localStorage.getItem(tokenKey);

        // Se non c'è il token: server presumibilmente ok ma utente non autenticato
        if (!token) {
          setIsServerOnline(true);
          if (redirectOnUnauthorized && !navOnceRef.current) {
            navOnceRef.current = true;
            if (typeof onUnauthorized === 'function') onUnauthorized();
            navigate(loginPath);
          }
          return;
        }

        const res = await fetch(pingUrl, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`, // niente x-tenant-id qui
          },
          signal: controller.signal,
          cache: 'no-store',
        });

        if (res.ok) {
          setIsServerOnline(true);
          return;
        }

        // 401/403 -> server raggiungibile ma token non valido/scaduto
        if (res.status === 401 || res.status === 403) {
          setIsServerOnline(true);
          if (redirectOnUnauthorized && !navOnceRef.current) {
            navOnceRef.current = true;
            if (typeof onUnauthorized === 'function') onUnauthorized();
            navigate(loginPath);
          }
          return;
        }

        // altri codici = down o errore lato server
        setIsServerOnline(false);
      } catch (err) {
        if (err.name !== 'AbortError') setIsServerOnline(false);
      }
    };

    // primo ping immediato + intervallo
    ping();
    const id = setInterval(ping, intervalMs);

    return () => {
      controller.abort();
      clearInterval(id);
    };
  }, [pingUrl, intervalMs, tokenKey, redirectOnUnauthorized, loginPath, onUnauthorized, navigate]);

  return isServerOnline;
}
